import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  Action,
  CommunicationService,
  Consents,
  eventsRepository,
} from "@identity/services";
import {
  AdsContext,
  ContextRepository,
  expiryDate,
  FandomContextGroups,
  Geo,
  Logger,
  LoggerTransformer,
  TimeUnit,
  TopLevelDomainExtractor,
} from "@identity/utils";
import { COOKIES, WINDOW } from "@identity/symbols";
import {
  USP_VALUES,
  UsPrivacy,
} from "../OptInService/consentProviders/one-trust/us-privacy";

interface AgeGatePayload {
  adult?: boolean;
  timestamp: number;
}

function isAgeGateOptOut(ageGateCookie: string | undefined): boolean {
  return ageGateCookie !== "1";
}

const AGE_GATE_COOKIE_NAME = "ag";

@singleton()
export class AgeGateHandler {
  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WINDOW) private readonly window: Window,
    @inject(AdsContext) private readonly context: AdsContext,
    @injectWithTransform(Logger, LoggerTransformer, "AgeGate")
    private readonly logger: Logger,
    @inject(Geo) private readonly geo: Geo,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
    @inject(ContextRepository)
    private readonly contextRepository: ContextRepository,
    @inject(UsPrivacy)
    private readonly usPrivacy: UsPrivacy,
  ) {}

  applies(optOut: boolean): boolean {
    if (!this.isEnabled() || optOut) {
      this.logger.info("disabled");
      return false;
    }

    const ageGateCookie = this.cookiesManager.get(AGE_GATE_COOKIE_NAME);
    const applies = ageGateCookie === undefined;
    this.logger.info("applies", applies);
    return applies;
  }

  resolveCoppaStatus(optOut: boolean): boolean {
    if (!this.isEnabled()) {
      this.setIsCoppaSubjectInContext(false);
      return false;
    }

    // If user opts out, we cannot override COPPA with AgeGate modal,
    // so we return true
    if (optOut) {
      this.setIsCoppaSubjectInContext(true);
      return true;
    }

    const ageGateCookie = this.cookiesManager.get(AGE_GATE_COOKIE_NAME);
    const coppaStatus = isAgeGateOptOut(ageGateCookie);
    this.setIsCoppaSubjectInContext(coppaStatus);
    return coppaStatus;
  }

  private isEnabled(): boolean {
    const wikiDirectedAtChildren = Boolean(
      this.context.get()?.targeting?.directedAtChildren,
    );
    const ageGateEnabled = Boolean(this.window.RLCONF?.wgAgeGateEnabled);

    return wikiDirectedAtChildren && ageGateEnabled;
  }

  async waitForAgeGateDataAndSetCookie(): Promise<Consents> {
    return new Promise((resolve) => {
      const ageGateCookie = this.cookiesManager.get(AGE_GATE_COOKIE_NAME);
      if (ageGateCookie !== undefined) {
        this.logger.info("Age gate cookie already set");
        const optOut = isAgeGateOptOut(ageGateCookie);
        resolve(this.updateConsents(optOut));
        return;
      }

      this.communicationService.on(
        eventsRepository.AGE_GATE_DATA,
        ({ payload }) => {
          this.setAgeGateCookie(payload);

          const optOut = !payload.adult;
          if (this.geo.getCountryCode() === "us" && optOut) {
            this.usPrivacy.createPrivacyCookie(USP_VALUES.yes);
            this.updateConsentCookie(optOut);
          }
          resolve(this.updateConsents(optOut));
        },
      );
    });
  }

  setAgeGateCookie(payload: AgeGatePayload | undefined): void {
    if (!payload) {
      this.logger.warn("Payload data missing, using defaults");
    }

    const encode = (value: boolean): string => (value ? "1" : "0");
    const adult = payload?.adult ?? false;

    this.cookiesManager.set(AGE_GATE_COOKIE_NAME, encode(adult), {
      domain: this.getTopLevelDomain(),
      expires: expiryDate(365, TimeUnit.DAYS),
    });
  }

  updateConsentCookie(optOut: boolean): void {
    this.communicationService.emit(
      new Action(eventsRepository.UPDATE_CONSENT, { optOut }),
    );
  }

  getTopLevelDomain(): string {
    const domain = this.window.location.hostname;
    return TopLevelDomainExtractor.extract(domain);
  }

  updateConsents(optOut: boolean): Consents {
    const countryCode = this.geo.getCountryCode();
    return {
      ccpaSignal: optOut,
      geoRequiresSignal: countryCode === "us",
      gdprConsent: !optOut,
      geoRequiresConsent: this.geo.isGdprCountry(),
    };
  }

  private setIsCoppaSubjectInContext(isCoppaSubject: boolean): void {
    this.contextRepository.set(
      FandomContextGroups.partners,
      "directedAtChildren",
      isCoppaSubject,
    );
    this.contextRepository.set(FandomContextGroups.partners, "blockthrough", {
      directedAtChildren: isCoppaSubject,
    });
  }
}
